import React from 'react';
import classnames from 'classnames';
import { Button, Typography } from '@jimdo/ui';
import { trackEvent, trackDWH } from '../../modules/tracking';
import { PackageType } from '../../redux/reducers/pricing';
import './package.scss';

import { PackageDescription, isComplexFeature, Feature } from '../../modules/getPackages';

interface PriceProps {
  price?: string;
  billingPeriod: string;
  subline: string;
}

interface PackageProps extends PackageDescription {
  ctaText: string;
  toggleButtonText: string;
  id: PackageType;
  onSelect: (packageId: PackageType) => void;
  isBestseller: boolean;
  ribbonText: string;
  onClick?: React.MouseEventHandler<void>;
}

interface State {
  isToggleOpen: boolean;
}

type OwnProps = PriceProps & PackageProps;

function getSubFeatureList(features: string[]): JSX.Element[] {
  return features.map((feature: string, index: number) => {
    return <li key={index}>{feature}</li>;
  });
}

export default class Package extends React.Component<OwnProps, State> {
  constructor(props: OwnProps) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.state = {
      isToggleOpen: false,
    };
  }

  public buildFeatureList(features: Feature[]): JSX.Element[] {
    return features.map((feature: Feature, index: number) => {
      const featureListElement = classnames('package-component__list-element', {
        'package-component__is-complex-feature': isComplexFeature(feature),
      });

      if (isComplexFeature(feature)) {
        return (
          <li className={featureListElement} key={feature.title}>
            <span className="package-component__feature">{feature.title}</span>
            <ul className="package-component__subfeature">{getSubFeatureList(feature.features)}</ul>
          </li>
        );
      }

      return (
        <li className={featureListElement} key={`${feature}-${index}`}>
          <span className="package-component__feature">{feature}</span>
        </li>
      );
    });
  }

  public render(): JSX.Element {
    const {
      name,
      description,
      subline,
      isBestseller,
      features,
      ctaText,
      toggleButtonText,
      price,
      billingPeriod,
      ribbonText,
    } = this.props;

    const featureList = this.buildFeatureList(features);
    const tile = classnames('package-component__tile', {
      'package-component__free': name === 'Free',
      'package-component__bestseller': isBestseller,
      'package-component__toggle-is-open': this.state.isToggleOpen,
    });

    const descriptionClass = classnames('package-component__description', {
      'package-component__pro': name === 'Pro' && !isBestseller,
    });

    const packageButton = classnames('package-component__package-button', {
      'package-component__package-button--outlined': !isBestseller,
    });

    return (
      <li className={tile}>
        <div className="package-component__content">
          <Typography
            className="package-component__title"
            variant="heading3"
            textTransform="uppercase"
            align="center"
          >
            {name}
          </Typography>
          <div className="package-component__bestseller-label">{ribbonText}</div>

          <div className="package-component__price-wrapper">
            <div className="package-component__price-tag">
              <span className="package-component__price">{price}</span>
              <span className="package-component__billing-period">{billingPeriod}</span>
            </div>
            <span className="package-component__subline">{subline}</span>
            <Button
              className={packageButton}
              buttonStyle="primary"
              onClick={this.handleClick('top')}
              noGutter={true}
            >
              {ctaText}
            </Button>
          </div>
          <div className={descriptionClass}>{description}</div>
          <div className="package-component__accordion-toggle" onClick={this.handleToggle}>
            {toggleButtonText}
          </div>
          <ul className="package-component__features">{featureList}</ul>
          <div className="package-component__button--center">
            <Button
              className={packageButton}
              buttonStyle="primary"
              onClick={this.handleClick('bottom')}
              noGutter={true}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </li>
    );
  }

  private handleClick = (buttonType: string) => {
    return (event: React.MouseEvent<HTMLButtonElement>): void => {
      this.props.onSelect(this.props.id);
      trackDWH('element', 'click', {
        buttonType,
        name: this.props.name,
      });
      trackEvent('CLICK', `${buttonType} ${this.props.name}`);
    };
  };

  private handleToggle(event: React.SyntheticEvent<HTMLDivElement>): void {
    this.setState({
      isToggleOpen: !this.state.isToggleOpen,
    });
  }
}
